import React from 'react';
import { EventType, EVENT_TYPE_CONFIGS, EventField } from '../../types/eventTypes';

interface DynamicEventFieldsProps {
    eventType: EventType;
    eventData: Record<string, unknown>;
    onChange: (data: Record<string, unknown>) => void;
}

interface DynamicFieldProps {
    field: EventField;
    value: unknown;
    onChange: (value: unknown) => void;
}

const DynamicField: React.FC<DynamicFieldProps> = ({ field, value, onChange }) => {
    const baseInputClass = `
    w-full bg-white border border-gray-300 rounded-lg 
    px-4 py-3 text-gray-900 placeholder-gray-400 
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm
  `;

    const renderField = () => {
        switch (field.type) {
            case 'text':
                return (
                    <input
                        type="text"
                        value={(value as string) || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                        className={baseInputClass}
                    />
                );

            case 'textarea':
                return (
                    <textarea
                        value={(value as string) || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                        rows={3}
                        className={`${baseInputClass} resize-none`}
                    />
                );

            case 'number':
                return (
                    <input
                        type="number"
                        value={(value as number) || ''}
                        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
                        placeholder={field.placeholder}
                        required={field.required}
                        className={baseInputClass}
                    />
                );

            case 'select':
                return (
                    <select
                        value={(value as string) || ''}
                        onChange={(e) => onChange(e.target.value)}
                        required={field.required}
                        className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
                    >
                        <option value="">-- Chọn --</option>
                        {field.options?.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                );

            case 'date':
                return (
                    <input
                        type="date"
                        value={(value as string) || ''}
                        onChange={(e) => onChange(e.target.value)}
                        required={field.required}
                        className={baseInputClass}
                    />
                );

            case 'checkbox':
                return (
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={(value as boolean) || false}
                            onChange={(e) => onChange(e.target.checked)}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{field.label}</span>
                    </label>
                );

            default:
                return null;
        }
    };

    // Don't render label for checkbox (handled in renderField)
    if (field.type === 'checkbox') {
        return <div className="flex items-center">{renderField()}</div>;
    }

    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                {field.label}
                {field.required && <span className="text-red-600 ml-1">*</span>}
            </label>
            {renderField()}
        </div>
    );
};

const DynamicEventFields: React.FC<DynamicEventFieldsProps> = ({
    eventType,
    eventData,
    onChange,
}) => {
    const config = EVENT_TYPE_CONFIGS[eventType];

    if (!config.fields || config.fields.length === 0) {
        return null;
    }

    const handleFieldChange = (fieldName: string, value: unknown) => {
        onChange({
            ...eventData,
            [fieldName]: value,
        });
    };

    return (
        <div className="space-y-3">
            {config.fields.map((field) => (
                <DynamicField
                    key={field.name}
                    field={field}
                    value={eventData[field.name]}
                    onChange={(value) => handleFieldChange(field.name, value)}
                />
            ))}
        </div>
    );
};

export default DynamicEventFields;
